import {
  AdsCampaign,
  DynamicPage,
  DynamicPageConnection,
  LinkedCampaign,
  Website,
} from '@betterfunnel/types';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { updateDynamicPage } from './DynamicPagesService';

export const linkedCampaignsColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'linkedCampaigns').withConverter(
    emptyConverter<LinkedCampaign>(),
  );
};

export const activeLinkedCampaignsQuery = (accountId: string) => {
  return query(
    linkedCampaignsColRef(accountId),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc'),
  );
};

export const createLinkedCampaign = async (
  accountId: string,
  adsCampaign: AdsCampaign,
  createdBy: string,
  dynamicPageId?: string,
): Promise<any> => {
  const linkedCampaignRef = linkedCampaignsColRef(accountId);

  const res = await addDoc(linkedCampaignRef, {
    name: adsCampaign.name,
    status: 'active',
    createdAt: Timestamp.now(),
    createdBy,
    externalAdsAccountId: adsCampaign.adsAccountId,
    externalCampaignId: adsCampaign.id,
    externalCampaignType: adsCampaign.type,
    externalCampaignProvider: adsCampaign.provider,
    ...(dynamicPageId ? { dynamicPageId: dynamicPageId } : {}),
  });

  if (dynamicPageId) {
    await updateDynamicPage(accountId, dynamicPageId, {
      [`connections.${adsCampaign.provider}-${adsCampaign.id}`]: {
        id: adsCampaign.id,
        type: 'adsCampaign',
        provider: adsCampaign.provider,
        name: adsCampaign.name,
        adsAccountId: adsCampaign.adsAccountId,
      } satisfies DynamicPageConnection,
    });
  }

  return res;
};

export const deleteLinkedCampaign = async (
  accountId: string,
  linkedCampaignId: string,
): Promise<any> => {
  const linkedCampaignRef = doc(
    linkedCampaignsColRef(accountId),
    linkedCampaignId,
  );
  const linkedCampaignDoc = await getDoc(linkedCampaignRef);

  const linkedCampaignData = linkedCampaignDoc.data();
  if (!linkedCampaignData) {
    return;
  }

  const dynamicPageId = linkedCampaignData.dynamicPageId;
  if (dynamicPageId) {
    await updateDynamicPage(accountId, dynamicPageId, {
      [`connections.${linkedCampaignData.externalCampaignProvider}-${linkedCampaignData.externalCampaignId}`]:
        deleteField(),
    });
  }

  return updateDoc(linkedCampaignRef, { status: 'deleted' });
};

export const suggestCampaignForAd = async (
  accountId: string,
  adId: string,
): Promise<{
  campaign: Partial<DynamicPage>;
  variants: Partial<Website>[];
}> => {
  const fn = httpsCallable<
    any,
    { campaign: Partial<DynamicPage>; variants: Partial<Website>[] }
  >(functions, 'accounts-campaigns-suggestCampaignForAd', { timeout: 600000 });

  const res = await fn({
    accountId,
    adId,
  });

  return res.data;
};

export const duplicateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ campaignId: string }> => {
  const fn = httpsCallable<any, { campaignId: string }>(
    functions,
    'accounts-campaigns-duplicateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};
